// Core packages
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';

// Third party packages
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import * as FileSaver from 'file-saver';
import { MatProgressButtonOptions } from 'mat-progress-buttons';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

// Custom packages
import { DocumentArchivesService } from 'src/app/modules/properties/document-archives.service';
import {
  canDownloadDocFilesZip,
  fallbackZipFileName,
  skippedFilesCount,
  skippedFilesMessage,
  SKIPPED_FILES_HEADER,
  ZipDownloadMessage,
  zipDownloadErrorMessage,
  zipFileNameFromContentDisposition,
} from 'src/app/modules/properties/document-files-zip.utils';
import { ConfigService } from 'src/app/shared/services/config.service';

/**
 * What a page passes when opening the dialog.
 *
 * Only the documents page passes the ids of "Scarica tutti": the other pages opening this dialog
 * pass the files alone, and there the button stays hidden.
 */
export interface DocumentDownloadDialogData {
  propertyId?: string;
  /** Entries of `data.document[]` of the row */
  documents?: any[];
  /** Archive of the property: `documentId` of the zip endpoint */
  documentArchiveId?: string;
  /** `docId` of the page row, the `_id` GET document-archives/:id returns: `subDocId` of the zip endpoint */
  docId?: string;
  /** `data.barCode` of the row: names the zip when the response does not */
  barCode?: string;
}

@Component({
  selector: 'app-document-download-dialog',
  templateUrl: './document-download-dialog.component.html',
  styleUrls: ['./document-download-dialog.component.scss']
})
export class DocumentDownloadDialogComponent implements OnInit, OnDestroy {
  lang = this.translateService.currentLang || this.translateService.defaultLang;

  // BEGIN - Add subUser form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly

  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant('common.cta.send'),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: 'primary',
    spinnerColor: 'primary',
    fullWidth: false,
    disabled: false,
    mode: 'indeterminate',
    buttonIcon: {
      fontSet: 'fal',
      fontIcon: 'fa-paper-plane',
      inline: true,
    },
  };
  // END - Add communication form

  dialogIsLoading = true;

  /** True when "Scarica tutti" is offered: see `canDownloadDocFilesZip` */
  canDownloadAll = false;

  /** True while the zip downloads: the button is disabled, so a second click asks for nothing */
  zipDownloading = false;

  private subscriptions: Subscription[] = [];

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<DocumentDownloadDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DocumentDownloadDialogData,
    public configService: ConfigService,

    private cookieService: CookieService,
    private translateService: TranslateService,
    private documentArchivesService: DocumentArchivesService,
    private toastrService: ToastrService,
  ) { }

  /**
   * Init component
   *
   */
  ngOnInit(): void {
    // Get current lang
    this.lang = this.cookieService.get('lang') || this.configService.defaultLang;

    this.initForm();
    this.canDownloadAll = canDownloadDocFilesZip(this.data);

    // Stop loading dialog
    this.dialogIsLoading = false;
  }

  /**
   * Destroy component
   *
   */
  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    // Set model
    this.model = this.data; //.documents;

    // Set fields
    this.fields = [
      ...this.fields,
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: "col-12 mb-0",
            type: "file-downloader",
            key: "documents",
            hideExpression: (model): boolean => !model || model.length === 0,
            templateOptions: {
              label: this.translateService.instant('properties.fields.documents'),
              required: false,
              bindLabel: "originalName",
              readonly: true,
            }
          },
        ],
      },
    ];
  }

  /**
   * Download every file of the document as one zip, built by the back-end.
   *
   * The zip is saved under the name the back-end sends in `Content-Disposition`, and a warning
   * tells how many files it could not include. The dialog stays open, as after a single download.
   *
   * @since 3.0.0
   */
  onDownloadAll(): void {
    if (!this.canDownloadAll || this.zipDownloading) {
      return;
    }
    this.zipDownloading = true;

    this.subscriptions.push(
      this.documentArchivesService
        .downloadDocFilesZip(this.data.documentArchiveId, this.data.docId)
        .pipe(finalize(() => (this.zipDownloading = false)))
        .subscribe(
          (res: HttpResponse<Blob> | Error) => {
            // ApiService emits a failure without status (network down, connection cut once the
            // zip had started) as a value, not as an error
            if (!(res instanceof HttpResponse) || !(res.body instanceof Blob)) {
              console.warn('Scarica tutti: zip download failed', { status: 0 });
              this.showMessage(zipDownloadErrorMessage(0, null, Date.now()), 'error');
              return;
            }
            const fileName =
              zipFileNameFromContentDisposition(res.headers.get('Content-Disposition')) ||
              fallbackZipFileName(this.data.barCode);
            FileSaver.saveAs(res.body, fileName);

            const skipped = skippedFilesMessage(
              skippedFilesCount(res.headers.get(SKIPPED_FILES_HEADER))
            );
            if (skipped) {
              this.showMessage(skipped, 'warning');
            }
          },
          (err: HttpErrorResponse) => {
            const status = err && typeof err.status === 'number' ? err.status : 0;
            const retryAfter = err && err.headers ? err.headers.get('Retry-After') : null;
            console.warn('Scarica tutti: zip download failed', { status });
            this.showMessage(zipDownloadErrorMessage(status, retryAfter, Date.now()), 'error');
          }
        )
    );
  }

  /**
   * Show a translated toast of the zip download
   *
   * @since 3.0.0
   */
  private showMessage(message: ZipDownloadMessage, level: 'error' | 'warning'): void {
    const text = this.translateService.instant(message.key, message.params);
    if (level === 'warning') {
      this.toastrService.warning(text);
    } else {
      this.toastrService.error(text);
    }
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   *
   */
  onClose(): void {
    this.dialogRef.close();
  }
}
